//External
import React, { useEffect, useMemo, useRef, useState } from "react";
//Internal
import IconChevronDown from "../icons/icons-chevron-down";
import { SEAT_THRESHOLDS, seatsForThreshold, thresholdMatches } from "./seatTiers";
import FilterDropdownPanel, { FilterEmptyOptions } from "./FilterDropdownPanel";
import useUnavailableOptions from "./useUnavailableOptions";
import { countMatchingVehicles } from "./availabilityPool";

/*******************
@Purpose : "Seating Capacity" filter dropdown - single-select "X or more"
           tiers (radio buttons, not checkboxes), a live "Show N cars" count
           for the in-progress pick, and a Reset that empties the draft
           entirely (radios can't be unchecked by clicking them again).
           Same draft-then-apply pattern as PriceRangeFilterDropdown. Both
           the count and the zero-result tier hiding are computed instantly
           off the shared availability pool - no network request happens on
           open or on each pick.
@Parameter : {selectedSeats, setSelectedSeats, otherFilters, pool}
@Author : INIC
******************/
function SeatingCapacityFilterDropdown({
  selectedSeats,
  setSelectedSeats,
  otherFilters = {},
  pool,
  colorList,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [draftSeats, setDraftSeats] = useState(selectedSeats);
  const containerRef = useRef();

  /*******************
  @purpose : start each open with a fresh draft copied from the last applied
             selection, so re-opening doesn't carry over an abandoned edit
  @Author : INIC
  ******************/
  useEffect(() => {
    if (isOpen) {
      setDraftSeats(selectedSeats);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // "Show N cars" for the in-progress pick (combined with the other
  // already-wired filters) - recomputed instantly on every click, straight
  // off the pool.
  const previewCount = countMatchingVehicles(pool, { ...otherFilters, seats: draftSeats }, colorList);

  /*******************
  @purpose : empty the draft entirely (no threshold selected)
  @Author : INIC
  ******************/
  const handleReset = () => {
    setDraftSeats([]);
  };

  const handleApply = () => {
    setSelectedSeats(draftSeats);
    setIsOpen(false);
  };

  const appliedThreshold = useMemo(
    () => SEAT_THRESHOLDS.find((threshold) => thresholdMatches(threshold, selectedSeats)),
    [selectedSeats]
  );
  const triggerLabel = appliedThreshold ? `${appliedThreshold} or more` : "Seating Capacity";

  const isDraftEmpty = draftSeats.length === 0;

  const thresholdAvailabilityInputs = useMemo(
    () => SEAT_THRESHOLDS.map((threshold) => ({ key: threshold, values: seatsForThreshold(threshold) })),
    []
  );

  // Tiers that would show 0 vehicles if picked on their own (given every
  // other already-active filter) - hidden below rather than left clickable
  // as a dead end. The currently-picked tier stays visible even if it now
  // computes as unavailable, so the radio can still be cleared via Reset.
  const unavailableThresholds = useUnavailableOptions({
    pool,
    filterKey: "seats",
    options: thresholdAvailabilityInputs,
    otherFilters,
    colorList,
  });

  const visibleThresholds = SEAT_THRESHOLDS.filter(
    (threshold) => !unavailableThresholds.has(threshold) || thresholdMatches(threshold, draftSeats)
  );

  return (
    <div className="filter-dropdown" ref={containerRef}>
      <button
        type="button"
        className={`filter-dropdown-trigger ${appliedThreshold ? "has-selection" : ""} ${isOpen ? "is-open" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="filter-dropdown-trigger-label">{triggerLabel}</span>
        <IconChevronDown className={`reservation-chevron ${isOpen ? "is-open" : ""}`} />
      </button>

      <FilterDropdownPanel
        isOpen={isOpen}
        anchorRef={containerRef}
        onOutsideClose={() => setIsOpen(false)}
        className="filter-dropdown-panel--multiselect"
      >
        <div className="filter-options-list">
          {visibleThresholds.length === 0 && <FilterEmptyOptions />}
          {visibleThresholds.map((threshold) => {
            const checked = thresholdMatches(threshold, draftSeats);
            return (
              <label key={threshold} className="filter-checkbox-row">
                <input
                  type="radio"
                  name="seating-capacity-threshold"
                  checked={checked}
                  onChange={() => setDraftSeats(seatsForThreshold(threshold))}
                />
                <span className={`filter-radio-circle ${checked ? "is-checked" : ""}`}>
                  {checked && <span className="filter-radio-dot" />}
                </span>
                <span className="filter-checkbox-label">{threshold} or more</span>
              </label>
            );
          })}
        </div>

        <div className="filter-dropdown-footer">
          <button
            type="button"
            className="filter-reset-btn"
            disabled={isDraftEmpty}
            onClick={handleReset}
          >
            Reset
          </button>
          <button type="button" className="filter-apply-btn" onClick={handleApply}>
            {pool?.length
              ? `Show ${previewCount} ${previewCount === 1 ? "car" : "cars"}`
              : "Show cars"}
          </button>
        </div>
      </FilterDropdownPanel>
    </div>
  );
}

export default SeatingCapacityFilterDropdown;
